import { useRef, useState } from 'react'
import type { EventPhoto } from '../../api/ownerTypes'
import {
  completeEventPhoto,
  createEventPhotoUpload,
  deleteEventPhoto,
  reorderEventPhotos,
} from '../../api/owner'
import { prepare } from '../../upload/prepare'
import { putToStorage } from '../../upload/transport'
import { Spinner } from '../../components/icons'

/** Zoveel passen er op de landing; de server bewaakt dezelfde grens. */
const maxPhotos = 5

/**
 * "Foto's op de landing" uit ronde 20H, 20K en 20V.
 *
 * De eerste is de cover. Verplaatsen gaat met de pijltjes op de greep en niet
 * met slepen: op een telefoon vecht slepen met het scrollen van de pagina, en
 * met twee tot vijf foto's is één tik omhoog of omlaag sneller dan mikken.
 */
export function LandingPhotos({
  eventId,
  photos,
  onChanged,
}: {
  eventId: string
  photos: EventPhoto[]
  onChanged: () => Promise<unknown>
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function add(files: FileList | null) {
    const chosen = [...(files ?? [])].slice(0, maxPhotos - photos.length)

    if (chosen.length === 0) {
      return
    }

    setBusy(true)
    setError(null)

    try {
      for (const file of chosen) {
        await upload(eventId, file)
      }

      await onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Uploaden lukte niet.')
    } finally {
      setBusy(false)

      // Leegmaken, anders vuurt dezelfde keuze geen change meer af.
      if (input.current) {
        input.current.value = ''
      }
    }
  }

  async function move(index: number, direction: -1 | 1) {
    const next = [...photos]
    const target = index + direction

    if (target < 0 || target >= next.length) {
      return
    }

    ;[next[index], next[target]] = [next[target], next[index]]

    await reorderEventPhotos(
      eventId,
      next.map((photo) => photo.id),
    )

    await onChanged()
  }

  async function remove(photoId: string) {
    await deleteEventPhoto(eventId, photoId)
    await onChanged()
  }

  return (
    <div>
      <div className="mb-2.5 flex items-baseline justify-between">
        <p className="text-ink-faint text-[11px] tracking-[0.18em] uppercase lg:text-xs">
          Foto’s op de landing
        </p>
        <p className="text-ink-faint text-[13px]">
          {photos.length} van {maxPhotos}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="rounded-row border-line-card bg-surface flex items-center gap-3 border p-2.5 px-3"
          >
            <span
              aria-hidden="true"
              className={
                'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ' +
                (index === 0
                  ? 'bg-accent text-accent-ink'
                  : 'border-line-warm text-ink-soft border font-semibold')
              }
            >
              {index + 1}
            </span>

            <span className="placeholder-warm size-11.5 shrink-0 overflow-hidden rounded-[9px]">
              <img src={photo.url} alt="" className="h-full w-full object-cover" />
            </span>

            <span className="min-w-0 flex-1">
              {index === 0 ? (
                <span className="text-accent block text-sm font-semibold">Cover</span>
              ) : (
                <span className="text-ink-faint block truncate text-[13px]">
                  {orientationOf(photo)}
                </span>
              )}
            </span>

            {/* De greep uit het ontwerp, hier als twee echte knoppen. */}
            <span className="flex shrink-0 flex-col">
              <button
                type="button"
                aria-label="Naar boven"
                disabled={index === 0}
                onClick={() => void move(index, -1)}
                className="text-ink-ghost px-1 text-[11px] leading-none disabled:opacity-30"
              >
                ▲
              </button>
              <button
                type="button"
                aria-label="Naar beneden"
                disabled={index === photos.length - 1}
                onClick={() => void move(index, 1)}
                className="text-ink-ghost px-1 text-[11px] leading-none disabled:opacity-30"
              >
                ▼
              </button>
            </span>

            <button
              type="button"
              aria-label={`${photo.originalFileName} verwijderen`}
              onClick={() => void remove(photo.id)}
              className="border-line-warm text-ink-soft flex size-7 shrink-0 items-center justify-center rounded-full border text-[11px]"
            >
              ✕
            </button>
          </div>
        ))}

        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className="rounded-row border-line bg-surface flex items-center gap-3 border-[1.5px] border-dashed p-3.25 text-left"
          >
            <span className="bg-tint text-accent flex size-8.5 shrink-0 items-center justify-center rounded-[10px] text-[15px]">
              {busy ? <Spinner className="size-3.5" /> : '↑'}
            </span>
            <span>
              <span className="block text-[15px] font-semibold">
                {busy ? 'Bezig met uploaden…' : 'Foto toevoegen'}
              </span>
              <span className="text-ink-soft mt-0.5 block text-[13px]">Kan later ook nog</span>
            </span>
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="text-accent mt-2.5 text-sm">
          {error}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        onChange={(event) => void add(event.target.files)}
      />
    </div>
  )
}

/**
 * Wat er onder een foto staat als hij niet de cover is. Uit de verhouding, want
 * dat is het enige dat er voor de carousel toe doet.
 */
function orientationOf(photo: EventPhoto): string {
  if (!photo.width || !photo.height) {
    return 'Foto'
  }

  if (photo.width > photo.height) {
    return 'Liggend'
  }

  return photo.width < photo.height ? 'Staand' : 'Vierkant'
}

/**
 * Dezelfde flow als bij gastenmedia: op canvas naar 1600px, dan twee presigned
 * PUT's, dan afronden. Geen wachtrij eromheen — dit zijn er hoogstens vijf en
 * de eigenaar staat ernaar te kijken.
 */
async function upload(eventId: string, file: File): Promise<void> {
  const prepared = await prepare(file)

  const created = await createEventPhotoUpload(eventId, {
    fileName: file.name || 'landing.jpg',
    contentType: file.type || 'image/jpeg',
    sizeBytes: file.size,
    width: prepared.width,
    height: prepared.height,
  })

  await putToStorage(created.original.url, file, created.original.headers)
  await putToStorage(created.web.url, prepared.web, created.web.headers)

  await completeEventPhoto(eventId, created.photoId, {
    width: prepared.width,
    height: prepared.height,
  })
}
